import { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { VoucherResponse } from '@/app/types/voucher';
import { DataTableColumnHeader } from '@/components/ui/data-table-column-header';

// Helper function to format currency
const formatCurrency = (amount: number | undefined | null): string => {
  if (amount === undefined || amount === null) return '-';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR', // TODO: Make currency dynamic based on org settings
  }).format(Number(amount));
};

// Helper function to format date
const formatDate = (dateString: string | Date | undefined | null): string => {
  if (!dateString) return '-';
  try {
    // Assuming date string is YYYY-MM-DD from backend
    return format(new Date(dateString), 'dd/MM/yyyy');
  } catch (error) {
    return String(dateString); // Fallback
  }
};

export const paymentsOutColumns = (): ColumnDef<VoucherResponse>[] => [
  {
    accessorKey: 'transaction_date',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Date" />,
    cell: ({ row }) => <span>{formatDate(row.getValue('transaction_date'))}</span>,
    enableColumnFilter: true,
    meta: {
      label: 'Date',
      variant: 'dateRange',
      placeholder: 'Filter by date...',
    },
  },
  {
    accessorKey: 'voucher_number',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Payment #" />,
    cell: ({ row }) => <span className="font-medium">{row.getValue('voucher_number')}</span>,
    enableColumnFilter: true,
    meta: {
      label: 'Payment #',
      variant: 'text',
      placeholder: 'Search payment number...',
    },
  },
  {
    accessorKey: 'reference_number',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Reference #" />,
    cell: ({ row }) => <span>{row.getValue('reference_number') || '-'}</span>,
    enableColumnFilter: true,
    meta: {
      label: 'Reference #',
      variant: 'text',
      placeholder: 'Search reference...',
    },
  },
  {
    id: 'party.name',
    accessorFn: (row) => row.party?.name || '',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Paid To" />,
    cell: ({ row }) => <span>{row.original.party?.name || '-'}</span>, // Handle potential null party
    enableSorting: false, // Disable sorting on nested object property directly
    enableColumnFilter: true,
    meta: {
      label: 'Paid To',
      variant: 'text',
      placeholder: 'Search supplier name...',
    },
  },
  {
    accessorKey: 'total_amount',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Amount" />,
    cell: ({ row }) => <span className="font-medium text-right block">{formatCurrency(row.getValue('total_amount'))}</span>,
    enableColumnFilter: true,
    meta: {
      label: 'Amount',
      variant: 'number',
      placeholder: 'Filter amount...',
    },
  },
  {
    accessorKey: 'is_cancelled',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
    cell: ({ row }) => (
      <Badge variant={row.getValue('is_cancelled') ? 'destructive' : 'secondary'}>
        {row.getValue('is_cancelled') ? 'Cancelled' : 'Active'}
      </Badge>
    ),
    enableColumnFilter: true,
    meta: {
      label: 'Status',
      variant: 'select',
      placeholder: 'All statuses',
      options: [
        { label: 'Active', value: 'active' },
        { label: 'Cancelled', value: 'cancelled' },
      ],
    },
    filterFn: (row, id, value) => { // Custom filter for boolean
        const isCancelled = row.getValue(id) === true;
        if (value === 'cancelled') return isCancelled;
        if (value === 'active') return !isCancelled;
        return true; // Show all if filter value is unexpected
    }
  },
];